import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, BillingCheckIcon, Box, ShimmerBox, Text } from '@common-fe/common-fe';

import { useGetPaymentMethods } from '@/modules/transaction/components/ReimburseMe/queries/useGetPaymentMethods.query';
import { PaymentMethod } from '@/modules/transaction/components/ReimburseMe/types/payment.types';
import { useMyMoneyNavigation } from '@/modules/UseMyMoney/hooks';
import { useDocumentOcrStore, useMyMoneyFormStore } from '@/modules/UseMyMoney/stores';
import { ClaimSummaryField } from '@/modules/UseMyMoney/UseMyMoney.types';
import { PaymentMethodType } from '@/modules/user/user.types';
import { getFullAddress } from '@/utils';

const ProviderPaymentMethodContainer = () => {
  const { t } = useTranslation();
  const { values, handleSetFormValues, handleSetState } = useMyMoneyFormStore();
  const { ocrDocument } = useDocumentOcrStore();
  const { handleSetActiveField } = useMyMoneyNavigation();

  const {
    isLoading: isPaymentMethodsLoading,
    formattedData: paymentMethods,
  } = useGetPaymentMethods();

  const provider = useMemo(() => ocrDocument?.providers?.find((provider) => provider?.id === values?.providerId),
    [ocrDocument?.providers, values?.providerId]);
  
  const selectedPaymentMethodForBillPay = useMemo(() => {
    return paymentMethods.find(
      (paymentMethod) =>
        paymentMethod?.paymentMethodOwner?.providerId === provider?.id &&
        paymentMethod?.type === PaymentMethodType.CHECK
    ) as PaymentMethod;
  }, [paymentMethods, provider]);

  // setup paymentMethodId
  useEffect(() => {
    if (!isPaymentMethodsLoading) {
      handleSetFormValues({ paymentMethodId: selectedPaymentMethodForBillPay?.id });
    }
  }, [handleSetFormValues, isPaymentMethodsLoading, selectedPaymentMethodForBillPay?.id]);

  const handleAddAddress = useCallback(() => {
    handleSetActiveField(ClaimSummaryField.PROVIDER);
    handleSetState({ automaticallyOpenProviderModal: true });
  }, [handleSetActiveField, handleSetState]);

  if (!provider) {
    return (
      <Box margin={{ top: 'spacing24' }}>
        <Text weight={700} data-testId="provider-payment-address_section_title">
          {t('Where do we send the payment?')}
        </Text>
        <Box
          margin={{ top: 'spacing8' }}
          pad="spacing16"
          background="border2"
          round="container1Round"
        >
          <Text color="textSecondary">
            {t('Select a provider to see their name and address here.')}
          </Text>
        </Box>
      </Box>
    );
  }

  if (isPaymentMethodsLoading) {
    return <ShimmerBox margin={{ top: 'spacing24' }} height="128px" />;
  }

  return (
    <Box data-testId="provider-payment-address_section" margin={{ top: 'spacing24' }}>
      <Text weight={700} data-testId="provider-payment-address_section_title">
        {t('Where do we send the payment?')}
      </Text>

      <Box
        data-testId="provider-payment-address"
        margin={{ top: 'spacing8' }}
        pad="spacing16"
        background="border2"
        round="container1Round"
      >
        <Box direction="row" align="center">
          <BillingCheckIcon color="iconAccent" size="24px" />
          <Box margin={{ left: 'spacing12' }}>
            <Text
              data-testId="provider-payment-address_provider-name"
              weight={700}
              size="20px" 
              style={{ lineHeight: '28px' }}
            >
              {provider?.name}
            </Text>
            {selectedPaymentMethodForBillPay ? (
              <Text data-testId="provider-payment-address_provider-address">
                {getFullAddress(selectedPaymentMethodForBillPay?.address)}
              </Text>
            ) : (
              <Text
                data-testId="provider-payment-address_provider-address_empty-state"
                color="danger"
              >
                {t('Action required: Add billing address to submit your claim')}
              </Text>
            )}
          </Box>

          {selectedPaymentMethodForBillPay ? null : (
            <AppButton
              testId="provider-payment-address_add-address"
              buttonType="secondary"
              onClick={handleAddAddress}
            >
              {t('Add Address')}
            </AppButton>
          )}
        </Box>

        {selectedPaymentMethodForBillPay ? (
          <Text
            data-testId="provider-payment-address_hint-title"
            margin={{ top: 'spacing8' }}
            color="textSecondary"
          >
            {t('If it\'s not correct, please update the Retailer/ Provider above.')}
          </Text>
        ) : null}
      </Box>
    </Box>
  );
};

export default ProviderPaymentMethodContainer;
